import React from 'react';

const signals = [
    { label: 'Sleep Hours', value: 0.72, color: '#f97316' },
    { label: 'Soreness (3d)', value: 0.65, color: '#f97316' },
    { label: 'Load Spike', value: 0.58, color: '#eab308' },
    { label: 'HRV', value: 0.44, color: '#eab308' },
    { label: 'Mental Fatigue', value: 0.38, color: '#22c55e' },
];

const painRegions = [
    { label: 'L Hamstring', x: 37, y: 134, w: 14, h: 28, color: '#f97316', level: 6, back: true },
    { label: 'Lower Back', x: 50, y: 88, w: 26, h: 18, color: '#ef4444', level: 7, back: true },
    { label: 'R Knee', x: 63, y: 158, w: 13, h: 12, color: '#eab308', level: 4, back: false },
];

const BodySilhouette: React.FC = () => (
    <g fill="#525252" opacity={0.3}>
        <ellipse cx="50" cy="18" rx="12" ry="14" />
        <rect x="45" y="32" width="10" height="8" rx="2" />
        <rect x="33" y="40" width="34" height="60" rx="5" />
        <rect x="20" y="44" width="11" height="42" rx="4" />
        <rect x="69" y="44" width="11" height="42" rx="4" />
        <rect x="34" y="102" width="14" height="54" rx="4" />
        <rect x="52" y="102" width="14" height="54" rx="4" />
        <rect x="34" y="158" width="13" height="36" rx="4" />
        <rect x="53" y="158" width="13" height="36" rx="4" />
    </g>
);

const MockInjuryRiskPreview: React.FC = () => {
    const riskScore = 67;
    const circumference = 2 * Math.PI * 40;
    const progress = (riskScore / 100) * circumference;

    return (
        <div className="border border-neutral-700 bg-neutral-900/80 max-w-2xl mx-auto">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-700 bg-neutral-900">
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-600" />
                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-600" />
                    <div className="w-2.5 h-2.5 rounded-full bg-neutral-600" />
                </div>
                <div className="flex-1 mx-4">
                    <div className="bg-neutral-800 h-5 flex items-center px-3 text-[10px] text-gray-500 font-mono">
                        app.cgmsports.com/athletes/jordan-chen
                    </div>
                </div>
            </div>

            {/* Dashboard content */}
            <div className="p-5 space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">Injury Risk Assessment</div>
                        <div className="text-sm font-bold text-white mt-0.5">Jordan Chen &middot; Mar 10, 2026</div>
                    </div>
                    <div className="text-[10px] text-orange-400 border border-orange-500/30 bg-orange-500/10 px-2 py-0.5 font-medium">
                        Elevated
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Left: Risk gauge + signals */}
                    <div className="space-y-4">
                        {/* Circular gauge */}
                        <div className="flex items-center gap-4">
                            <svg width="90" height="90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="#262626" strokeWidth="8" />
                                <circle
                                    cx="50" cy="50" r="40"
                                    fill="none"
                                    stroke="#f97316"
                                    strokeWidth="8"
                                    strokeDasharray={`${progress} ${circumference}`}
                                    strokeLinecap="butt"
                                    transform="rotate(-90 50 50)"
                                />
                                <text x="50" y="46" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">{riskScore}</text>
                                <text x="50" y="60" textAnchor="middle" fill="#a3a3a3" fontSize="8">/100</text>
                            </svg>
                            <div>
                                <div className="text-xs text-gray-400">Confidence</div>
                                <div className="text-sm font-semibold text-white">82%</div>
                                <div className="text-[10px] text-gray-500 mt-1">9 of 11 signals</div>
                            </div>
                        </div>

                        {/* Signal bars */}
                        <div className="space-y-2.5">
                            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Top Contributing Signals</div>
                            {signals.map((s) => (
                                <div key={s.label} className="space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-gray-300">{s.label}</span>
                                        <span className="font-mono text-gray-400">{(s.value * 100).toFixed(0)}%</span>
                                    </div>
                                    <div className="h-1.5 bg-neutral-800 w-full">
                                        <div className="h-full" style={{ width: `${s.value * 100}%`, backgroundColor: s.color }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Body region map */}
                    <div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-3">Body Region Risk Map</div>
                        <div className="flex gap-3 justify-center">
                            {/* Front view */}
                            <div className="text-center">
                                <svg viewBox="0 0 100 200" className="w-[70px] h-[140px]">
                                    <BodySilhouette />
                                    {painRegions.filter(r => !r.back).map(r => (
                                        <g key={r.label}>
                                            <rect
                                                x={r.x - r.w / 2} y={r.y - r.h / 2}
                                                width={r.w} height={r.h} rx={3}
                                                fill={r.color} fillOpacity={0.4}
                                                stroke={r.color} strokeWidth={1}
                                            />
                                            <text x={r.x} y={r.y + 4} textAnchor="middle" fontSize="9" fontWeight="bold" fill={r.color}>
                                                {r.level}
                                            </text>
                                        </g>
                                    ))}
                                </svg>
                                <span className="text-[10px] text-gray-500">Front</span>
                            </div>
                            {/* Back view */}
                            <div className="text-center">
                                <svg viewBox="0 0 100 200" className="w-[70px] h-[140px]">
                                    <BodySilhouette />
                                    {painRegions.filter(r => r.back).map(r => (
                                        <g key={r.label}>
                                            <rect
                                                x={r.x - r.w / 2} y={r.y - r.h / 2}
                                                width={r.w} height={r.h} rx={3}
                                                fill={r.color} fillOpacity={0.4}
                                                stroke={r.color} strokeWidth={1}
                                            />
                                            <text x={r.x} y={r.y + 4} textAnchor="middle" fontSize="9" fontWeight="bold" fill={r.color}>
                                                {r.level}
                                            </text>
                                        </g>
                                    ))}
                                </svg>
                                <span className="text-[10px] text-gray-500">Back</span>
                            </div>
                        </div>

                        {/* Region detail cards */}
                        <div className="mt-3 space-y-1.5">
                            {painRegions.sort((a, b) => b.level - a.level).map(r => (
                                <div key={r.label} className="flex items-center justify-between bg-neutral-800/50 border border-neutral-700/50 px-3 py-1.5 text-[11px]">
                                    <span className="text-gray-300">{r.label}</span>
                                    <span className="font-bold" style={{ color: r.color }}>{r.level}/10</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Coach actions */}
                <div className="border-t border-neutral-700 pt-3">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Recommended Actions</div>
                    <div className="space-y-1.5">
                        <div className="flex items-start gap-2 text-[11px]">
                            <span className="text-orange-400 mt-0.5">&#9654;</span>
                            <span className="text-gray-300">Reduce lower-body volume — hamstring and lower back pain trending up over 3 days</span>
                        </div>
                        <div className="flex items-start gap-2 text-[11px]">
                            <span className="text-yellow-400 mt-0.5">&#9654;</span>
                            <span className="text-gray-300">Review sleep patterns — averaging 5.8h over last week vs 7.2h baseline</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MockInjuryRiskPreview;
